// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — ACHIEVEMENT WRITE PATH  (progression slice)
// ────────────────────────────────────────────────────────────────────
// The Firebase write/read layer for achievements. This is the twin of the
// cosmetic grant slice in progression.js — append-only, namespaced,
// permanent provenance, idempotent. Same engine, same safety guarantees.
//
// ── HOW TO INTEGRATE ─────────────────────────────────────────────────
// Two options, both fine:
//   A) Paste this whole block at the END of progression.js (after the
//      cosmetics slice). Then delete the three import lines below — db and
//      the firebase helpers are already imported at the top of that file.
//   B) Keep it as its own module `achievementWrites.js` and import from it.
//      Leave the imports as-is.
//
// Either way the storage path is profiles/{uid}/achievements/{id}, sitting
// right beside unlockedCosmetics — Cloudflare-verifiable, shared across
// every IE game on the terror-tcg Firebase project.
//
// ── DATA SHAPE (per achievement) ─────────────────────────────────────
//   profiles/{uid}/achievements/{id} = {
//     earnedAt: <ms timestamp>,   // permanent — never overwritten
//     game:     "terror",          // origin namespace (audit/provenance)
//     rarity:   "legendary",       // snapshot of rarity at earn time
//     source:   "terror:beat_jynxi:storyMode", // where/why it fired
//   }
//
// Append-only: re-granting an owned achievement is a no-op. earnedAt and
// source are permanent — provenance never changes. This is the same
// guarantee that protects collections and cosmetics from silent resets.
// ════════════════════════════════════════════════════════════════════

import { ref, get, set, onValue } from "firebase/database";
import { db } from "./firebase";
import {
  ACHIEVEMENTS,
  isValidAchievement,
  getAchievement,
  deriveMetaAchievements,
} from "./achievements";

// ── GRANT ─────────────────────────────────────────────────────────────
// Award one achievement. Append-only & idempotent: if already earned, the
// original record is preserved untouched and this returns alreadyEarned.
//
// After a successful grant, this AUTOMATICALLY checks the empire meta tier
// (Two Worlds, Citizen) and grants any newly-qualified meta achievements
// too — so the universe acknowledges itself without every call site
// having to remember to do it.
//
// source: optional extra provenance string (e.g. "storyMode:chapter3").
//         The achievement's own id is always recorded as the base source.
//
// Returns: {
//   success, alreadyEarned, reason?,
//   granted: [ids...],   // everything newly written this call (base + meta)
// }
export const grantAchievement = async (uid, achievementId, source = "") => {
  if (!uid || !achievementId) {
    return { success: false, reason: "Missing uid or achievementId.", granted: [] };
  }
  if (!isValidAchievement(achievementId)) {
    return { success: false, reason: `Unknown achievement: ${achievementId}`, granted: [] };
  }

  const def = getAchievement(achievementId);
  const achRef = ref(db, `profiles/${uid}/achievements/${achievementId}`);
  const snap = await get(achRef);

  if (snap.exists()) {
    // Append-only: never overwrite a permanent record. But STILL run the
    // meta check below in case earlier grants left the player newly
    // qualified for a meta achievement they don't yet hold.
    const metaGranted = await _grantMeta(uid);
    return {
      success: true,
      alreadyEarned: true,
      granted: metaGranted,
    };
  }

  await set(achRef, {
    earnedAt: Date.now(),
    game: def.game,
    rarity: def.rarity,
    source: source ? `${achievementId}:${source}` : achievementId,
  });

  const metaGranted = await _grantMeta(uid);

  return {
    success: true,
    alreadyEarned: false,
    granted: [achievementId, ...metaGranted],
  };
};

// Internal: read the player's full achievement map, derive which empire
// meta achievements they now qualify for, and write any missing ones.
// Idempotent and safe to call repeatedly. Returns ids newly written.
const _grantMeta = async (uid) => {
  const map = await getAchievements(uid);
  const toGrant = deriveMetaAchievements(map);
  const written = [];
  for (const id of toGrant) {
    const def = getAchievement(id);
    if (!def) continue;
    const r = ref(db, `profiles/${uid}/achievements/${id}`);
    const s = await get(r);
    if (s.exists()) continue; // someone beat us to it
    await set(r, {
      earnedAt: Date.now(),
      game: def.game,
      rarity: def.rarity,
      source: `${id}:auto`,
    });
    written.push(id);
  }
  return written;
};

// ── READ ──────────────────────────────────────────────────────────────

// One-shot read of the full achievement map.
// Shape: { [id]: { earnedAt, game, rarity, source } }
export const getAchievements = async (uid) => {
  if (!uid) return {};
  const snap = await get(ref(db, `profiles/${uid}/achievements`));
  return snap.val() || {};
};

// Live listener for the achievement map. Fires with the full map on every
// change. Returns the unsubscribe function. (Twin of listenToUnlocked.)
export const listenToAchievements = (uid, callback) => {
  if (!uid) {
    callback({});
    return () => {};
  }
  return onValue(ref(db, `profiles/${uid}/achievements`), (snap) => {
    callback(snap.val() || {});
  });
};

// Quick check: does this uid hold this achievement?
export const hasAchievement = async (uid, achievementId) => {
  if (!uid || !achievementId) return false;
  const snap = await get(ref(db, `profiles/${uid}/achievements/${achievementId}`));
  return snap.exists();
};

// ── CROSS-GAME GATE (the Cloudflare-verifiable check) ─────────────────
// Convenience wrapper: fetch the player's achievements and evaluate a
// reward gate in one call. Most UI should prefer the pure isRewardUnlocked
// from achievements.js (no Firebase round-trip) when it already holds the
// map via the hook — but this exists for one-shot server-style checks.
export const checkRewardUnlocked = async (uid, rewardId) => {
  const { isRewardUnlocked } = await import("./achievements");
  const map = await getAchievements(uid);
  return isRewardUnlocked(map, rewardId);
};

// ════════════════════════════════════════════════════════════════════
// END OF IE ACHIEVEMENTS — SHARED WRITE PATH
// ════════════════════════════════════════════════════════════════════
